import React, { useRef } from 'react';
import { SlControlPlay, SlControlForward, SlControlRewind } from "react-icons/sl";

const SegmentEditor = ({ 
  segments, 
  editedSegments, 
  setEditedSegments, 
  editedSegment, 
  setEditedSegment, 
  audioref,
  subject,
  file,
  onSave,
  onCancel 
}) => {
  const [saving, setSaving] = React.useState(false);
  const currentHandlerRefEd = useRef(null);

  const newreachedEndEd = (segment) => {
    const handlered = (event) => {
      if (event.target.currentTime >= segment.end) {
        audioref.current.pause();
        audioref.current.currentTime = segment.start;
        audioref.current.removeEventListener('timeupdate', handlered);
        currentHandlerRefEd.current = null;
      }
    };
    return handlered;
  };

  const handlered = newreachedEndEd(editedSegment);

  const saveEditedSegments = async () => {
    setSaving(true);
    try {
      await onSave(editedSegments);
    } finally {
      setSaving(false);
    }
  };

  const updateSegmentText = (segmentIndex, wordIndex, newText) => {
    const newSegments = [...editedSegments];
    const segmentToUpdate = { ...newSegments[segmentIndex] };
    
    // Update the specific word
    segmentToUpdate.words[wordIndex].text = newText;
    
    // Update the full segment text
    segmentToUpdate.text = segmentToUpdate.words.map(word => word.text).join(" ");
    
    newSegments[segmentIndex] = segmentToUpdate;
    setEditedSegments(newSegments);
    
    // If this is the currently displayed segment, update it too
    if (segmentIndex === editedSegment.id) {
      setEditedSegment(segmentToUpdate);
    }
  };

  const updateSegmentTime = (segmentIndex, field, value) => {
    const newSegments = [...editedSegments];
    const segmentToUpdate = { ...newSegments[segmentIndex] };
    
    segmentToUpdate[field] = parseFloat(value);
    
    // Ensure start time is before end time
    if (field === 'start' && segmentToUpdate.start >= segmentToUpdate.end) {
      segmentToUpdate.end = segmentToUpdate.start + 0.1;
    } else if (field === 'end' && segmentToUpdate.end <= segmentToUpdate.start) {
      segmentToUpdate.start = segmentToUpdate.end - 0.1;
    }
    
    newSegments[segmentIndex] = segmentToUpdate;
    setEditedSegments(newSegments);
    
    // Update adjacent segments if necessary
    if (field === 'end' && segmentIndex < newSegments.length - 1) {
      const nextSegment = newSegments[segmentIndex + 1];
      if (nextSegment.start < segmentToUpdate.end) {
        nextSegment.start = segmentToUpdate.end;
        newSegments[segmentIndex + 1] = { ...nextSegment };
      }
    }
    
    if (segmentIndex === editedSegment.id) {
      setEditedSegment(segmentToUpdate);
    }
  };

  if (!editedSegments || !editedSegment) return null;

  return (
<div className="flex flex-col min-h-0 max-h-[75vh] overflow-y-auto gap-4 p-6 bg-white border-2 border-blue-300 rounded-xl shadow-lg">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800">Segment Editor</h3>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={saveEditedSegments}
            disabled={saving}
            className={`px-4 py-2 rounded-lg transition-colors ${
              saving 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Segment Navigation */}
      <div className="flex items-center justify-center gap-4">
        <button
          className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          onClick={() => {
            if (audioref.current) audioref.current.pause();
            if (currentHandlerRefEd.current) {
              audioref.current.removeEventListener('timeupdate', currentHandlerRefEd.current);
            }
            if (editedSegment.id > 0) {
              const prevSegment = editedSegments[editedSegment.id - 1];
              audioref.current.currentTime = prevSegment.start;
              setEditedSegment(prevSegment);
            }
          }}
        >
          <SlControlRewind className="text-gray-700" />
        </button>

        <button
          className="p-3 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
          onClick={() => {
            if (audioref.current) {
              if (audioref.current.paused) {
                audioref.current.currentTime = editedSegment.start;
                audioref.current.play();
              } else {
                audioref.current.pause();
                audioref.current.currentTime = editedSegment.start;
                audioref.current.play();
              }

              if (currentHandlerRefEd.current) {
                audioref.current.removeEventListener('timeupdate', currentHandlerRefEd.current);
              }
              currentHandlerRefEd.current = handlered;
              audioref.current.addEventListener("timeupdate", currentHandlerRefEd.current);
            }
          }}
        >
          <SlControlPlay className="text-blue-700" />
        </button>

        <button
          className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          onClick={() => {
            if (audioref.current) audioref.current.pause();
            if (currentHandlerRefEd.current) {
              audioref.current.removeEventListener('timeupdate', currentHandlerRefEd.current);
            }
            if (editedSegment.id < editedSegments.length - 1) {
              const nextSegment = editedSegments[editedSegment.id + 1];
              audioref.current.currentTime = nextSegment.start;
              setEditedSegment(nextSegment);
            }
          }}
        >
          <SlControlForward className="text-gray-700" />
        </button>
      </div>

      {/* Current Segment Info */}
      <div className="text-center">
        <span className="text-sm text-gray-600">
          Segment {editedSegment.id + 1} of {editedSegments.length}
        </span>
      </div>

      {/* Time Controls */}
      <div className="flex gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
          <input
            type="number"
            step="0.1"
            value={editedSegment.start}
            onChange={(e) => updateSegmentTime(editedSegment.id, 'start', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
          <input
            type="number"
            step="0.1"
            value={editedSegment.end}
            onChange={(e) => updateSegmentTime(editedSegment.id, 'end', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Word Editor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Segment Text</label>
        <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg">
          {editedSegment.words?.map((word, wordIndex) => (
            <div key={wordIndex} className="flex flex-col items-center">
              <input
                value={word.text}
                size={Math.max(word.text.length, 1)}
                onChange={(e) => updateSegmentText(editedSegment.id, wordIndex, e.target.value)}
                className="border rounded px-2 py-1 text-center min-w-16 focus:ring-2 focus:ring-blue-500"
              />
              <div className="text-xs text-gray-500 mt-1">
                {word.start.toFixed(1)}s
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Full Text Preview */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Text</label>
        <div className="p-3 bg-white border border-gray-300 rounded-lg text-gray-700">
          {editedSegment.text}
        </div>
      </div>
    </div>
  );
};

export default SegmentEditor;